import {useState} from "react"
import { CustomModal, ReadOnlyBadge } from "@components"
import { mySqlToLocal } from "@helpers";

import {
  Button, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
} from "@heroui/react";

import { ArrowsRightLeftIcon } from "@heroicons/react/24/solid";
import PropTypes from 'prop-types';

const ManageAvailability = ({ 
  handleClose, 
  handleSave, 
  isReadOnly, 
  isSaving, 
  show, 
  slots, 
  title 
}) => {
  const [slotAlterations, setSlotAlterations] = useState([])
  const isDisabled = slotAlterations.length === 0 || isReadOnly
  
  const getSlotCounts = () => {
    const counts = {
      available: 0,
      booked: 0,
      disabled: 0,
      total: slots.length,
    }

    slots.forEach((slot) => (counts[getSlotStatus(slot).toLowerCase()]++))

    return counts
  }

  const getSlotStatus = (slot) => {
    return slotAlterations.find((alteration) => alteration.id === slot.id)?.status || slot.slot_status
  }

  const toggleStatus = (slot, status) => {
    if (!["Available", "Disabled"].includes(status) || isReadOnly) {
      return null
    }

    const newSlotStatus = status === "Available" ? "Disabled" : "Available"
    const newSlotAlterations = [...slotAlterations].filter((alteration) => slot.id !== alteration.id)

    if (slot.slot_status !== newSlotStatus) {
      setSlotAlterations([...newSlotAlterations, { id: slot.id, status: newSlotStatus }])
    } 
    
    if (slot.slot_status === newSlotStatus) {
      setSlotAlterations(newSlotAlterations)
    }
  }

  const {available, booked, disabled, total} = getSlotCounts()

  return (
    <CustomModal
      isOpen={show}
      size="xl"
      handleClose={() => handleClose()}
      scrollBehavior="inside"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>Manage Your Availability</ModalHeader>
          
            <ModalBody className="gap-1">
              <div className="flex items-center mb-1">
                <h1 className="mt-2 mr-4 text-lg font-semibold">{title}</h1>
                {isReadOnly && <ReadOnlyBadge size="lg" />}
              </div>
                
              <p className="mb-2 text-content2-foreground">Click on slot status of the relevant row to toggle between "Disabled" and "Available" states. Please note "Booked" slots can not be altered here.</p>
              
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex flex-col flex-grow p-3 text-center border-l-8 border-indigo-400 rounded-md shadow-lg bg-content2">
                  <span className="text-3xl font-semibold">{total}</span>
                  <span className="text-sm">Total</span>
                </div>

                <div className="flex flex-col flex-grow p-3 text-center border-l-8 rounded-md shadow-lg bg-content2 border-available">
                  <span className="text-3xl font-semibold">{available}</span>
                  <span className="text-sm">Available</span>
                </div>
                
                <div className="flex flex-col flex-grow p-3 text-center border-l-8 rounded-md shadow-lg bg-content2 border-booking">
                  <span className="text-3xl font-semibold">{booked}</span>
                  <span className="text-sm">Booked</span>
                </div>

                <div className="flex flex-col flex-grow p-3 text-center border-l-8 rounded-md shadow-lg bg-content2 border-disabled">
                  <span className="text-3xl font-semibold">{disabled}</span>
                  <span className="text-sm">Disabled</span>
                </div>
              </div>

              <div className="flex flex-col">
                {slots.map((slot) => {
                  const status = getSlotStatus(slot)
  
                  const classes = {
                    "Booked":  "bg-booking text-white",
                    "Available": `bg-available text-white ${isReadOnly ? "" : "cursor-pointer hover:bg-available hover:bg-[#8ee47a]"}`,
                    "Disabled": `bg-disabled text-white ${isReadOnly ? "" : "cursor-pointer hover:bg-disabled hover:bg-gray-300"}`,
                  }

                  return (
                    <div className="flex items-center border-b-3 border-content1" key={slot.id}>
                      <div className={`px-3 py-2 mr-[2px] font-semibold text-center bg-gray-400 text-white rounded-l-md w-20`}>
                        {mySqlToLocal(slot.starts_at).format("HH:mm")}
                      </div>

                      <div className={`flex-grow text-center px-3 py-2 rounded-r-md font-semibold ${classes[status]}`} onClick={() => toggleStatus(slot, status)}>
                        <div className="flex items-center justify-between">
                          {status}

                          {status !== "Booked" && !isReadOnly && (
                            <div>
                              <ArrowsRightLeftIcon className="w-6 h-6"/>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </ModalBody>

            <ModalFooter>
              <div className="flex justify-between w-full">
                <Button onClick={() => handleClose()} tabIndex={-1}>Cancel</Button>
                <Button color="secondary" onClick={() => handleSave(slotAlterations)} tabIndex={-1} isDisabled={isDisabled} isLoading={isSaving}>Save</Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </CustomModal>
  )
}

ManageAvailability.propTypes = {
  slots: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      starts_at: PropTypes.string.isRequired,
      ends_at: PropTypes.string.isRequired,
      slot_status: PropTypes.string.isRequired,
      student: PropTypes.string,
    })
  ).isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  isSaving: PropTypes.bool,
  show: PropTypes.bool,
  title: PropTypes.string.isRequired,
}

export default ManageAvailability